/**
 * Edit › Preferences (A-19): every persisted editor setting, grouped by
 * category, edited with the same prop widgets as the Inspector through
 * `useSettingBinding` (D-4). Session-only settings (the active tool, view
 * mode) live in the toolbars instead, as in Unreal.
 */
import { useEffect, useRef } from "react";
import type { PropType } from "../../world/schema/propTypes";
import { useSettingBinding, useThemeBinding } from "../bindings/bindings";
import { useEditor } from "../EditorContext";
import { SETTINGS, settingKeys } from "../settings/EditorSettings";
import type { SettingDefinition, SettingKey } from "../settings/EditorSettings";
import { PropWidget } from "../widgets/PropWidget";
import { useCloseOnEscape } from "../ui/useCloseOnEscape";
import "../ui/modal.css";
import "./preferences.css";
import { SYSTEM_THEME } from "../theme/themeBoot";

export function PreferencesDialog({ onClose }: { onClose: () => void }) {
  const dialogRef = useRef<HTMLDivElement>(null);

  // Focus moves into the dialog on open (WAI-ARIA dialog pattern).
  useEffect(() => {
    dialogRef.current?.querySelector<HTMLElement>("input, select, button")?.focus();
  }, []);

  useCloseOnEscape(onClose);

  return (
    <div
      className="kn-modal__scrim"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="kn-modal kn-preferences"
        role="dialog"
        aria-modal="true"
        aria-labelledby="kn-preferences-title"
      >
        <header className="kn-modal__header">
          <h2 id="kn-preferences-title">Preferences</h2>
          <button type="button" className="kn-modal__close" onClick={onClose}>
            Close
          </button>
        </header>
        <div className="kn-preferences__body">
          <fieldset className="kn-preferences__group" aria-label="Appearance">
            <legend>Appearance</legend>
            <div className="kn-preferences__rows">
              <ThemeRow />
            </div>
          </fieldset>
          {settingGroups().map(([category, keys]) => (
            <fieldset key={category} className="kn-preferences__group" aria-label={category}>
              <legend>{category}</legend>
              <div className="kn-preferences__rows">
                {keys.map((key) => (
                  <SettingRow key={key} settingKey={key} />
                ))}
              </div>
            </fieldset>
          ))}
        </div>
      </div>
    </div>
  );
}

function SettingRow({ settingKey }: { settingKey: SettingKey }) {
  const binding = useSettingBinding(settingKey);
  const { prop } = SETTINGS[settingKey];
  const id = `setting-${settingKey}`;
  const { label } = prop;

  return (
    <div className="kn-preferences__row">
      <label htmlFor={id}>{label}</label>
      <PropWidget
        id={id}
        label={label}
        prop={prop}
        value={binding.value}
        onChange={binding.onChange}
        onCommit={binding.onCommit}
        error={binding.error}
      />
    </div>
  );
}

/** The theme menu: System, then every registered theme, as an enum widget through `useThemeBinding`. */
function ThemeRow() {
  const { theme } = useEditor();
  const binding = useThemeBinding();
  const prop: PropType = {
    type: "enum",
    label: "Theme",
    options: [
      { value: SYSTEM_THEME, label: "System" },
      ...theme.all.map((option) => ({
        value: option.id,
        label: `${option.name} (${option.mode})`,
      })),
    ],
  };

  return (
    <div className="kn-preferences__row">
      <label htmlFor="setting-theme">Theme</label>
      <PropWidget
        id="setting-theme"
        label="Theme"
        prop={prop}
        value={binding.value}
        onChange={binding.onChange}
        onCommit={binding.onCommit}
        error={binding.error}
      />
    </div>
  );
}

/** Persisted settings grouped by category, in declaration order. */
function settingGroups(): [SettingDefinition["category"], SettingKey[]][] {
  const groups = new Map<SettingDefinition["category"], SettingKey[]>();
  for (const key of settingKeys()) {
    const { persist, category } = SETTINGS[key];
    if (!persist) continue;
    groups.set(category, [...(groups.get(category) ?? []), key]);
  }
  return [...groups];
}
